import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EmptyState from '@/components/feature/EmptyState';
import VideoPlayer from '@/pages/detail/components/VideoPlayer';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import type { Video } from '@/types/video';
import { formatCount, formatDate } from '@/utils/video';
import { useFavorites } from '@/hooks/useFavorites';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function DetailPage() {
  const { videoId = '' } = useParams();
  const allVideos = videos as Video[];
  const video = allVideos.find((v) => v.id === videoId);
  const category = categories.find((c) => c.id === video?.category);
  const { isFavorite, toggleFavorite } = useFavorites();

  useDocumentTitle({
    title: video ? `${video.title} - 成人快手` : '视频详情 - 成人快手',
    description: video ? `${video.title}，由${video.author}发布，${video.description.slice(0, 60)}` : '成人快手视频详情。',
    keywords: video ? `成人快手,${video.title},${video.tags.join(',')},短视频` : '成人快手,视频详情',
  });

  const related = useMemo(() => {
    if (!video) return [];
    return allVideos
      .filter((v) => v.category === video.category && v.id !== video.id)
      .sort((a, b) => b.views - a.views)
      .slice(0, 4);
  }, [allVideos, video]);

  if (!video) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-file-unknow-line"
          title="视频不存在或已下架"
          description="你访问的视频可能已被移除，返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const favorite = isFavorite(video.id);

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          ...(category ? [{ label: category.name, to: `/category/${category.id}` }] : []),
          { label: '视频详情' },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Main column */}
        <div className="lg:col-span-2">
          <VideoPlayer video={video} />

          <h1 className="mt-5 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
            {video.title}
          </h1>

          {/* Meta row */}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-500/20 text-secondary-300">
                <i className="ri-user-3-line text-sm" />
              </span>
              <span className="font-medium text-foreground-900">{video.author}</span>
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {formatDate(video.publishDate)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-play-circle-line" />
              {formatCount(video.views)} 次播放
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line" />
              {video.region}
            </span>
          </div>

          {/* Tags */}
          <div className="mt-4 flex flex-wrap gap-2">
            {video.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full bg-secondary-500/15 px-3 py-1 text-xs text-secondary-200 transition-colors hover:bg-secondary-500/25"
              >
                #{tag}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="mt-5 flex flex-wrap items-center gap-3 border-y border-background-200 py-4">
            <button
              type="button"
              onClick={() => toggleFavorite(video.id)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                favorite
                  ? 'bg-primary-500 text-foreground-950'
                  : 'border border-background-300/70 text-foreground-800 hover:border-primary-600 hover:text-primary-300'
              }`}
            >
              <i className={favorite ? 'ri-heart-3-fill text-base' : 'ri-heart-3-line text-base'} />
              {favorite ? '已收藏' : '收藏'}
            </button>
            <span className="flex items-center gap-2 rounded-full border border-background-300/70 px-5 py-2.5 text-sm text-foreground-700">
              <i className="ri-thumb-up-line text-base text-accent-400" />
              {formatCount(video.likes)} 点赞
            </span>
            {category && (
              <Link
                to={`/category/${category.id}`}
                className="ml-auto flex items-center gap-1 text-sm text-foreground-500 transition-colors hover:text-primary-300"
              >
                <i className="ri-arrow-left-line" />
                返回{category.name}分类
              </Link>
            )}
          </div>

          {/* Description */}
          <div className="mt-5 rounded-lg border border-background-200/70 bg-background-100 p-5">
            <h2 className="mb-2 font-heading text-base font-semibold text-foreground-950">视频简介</h2>
            <p className="text-sm leading-relaxed text-foreground-600">{video.description}</p>
            <p className="mt-4 rounded-md bg-background-200/50 px-3 py-2 text-xs leading-relaxed text-foreground-500">
              <i className="ri-information-line mr-1 text-accent-400" />
              本站为前端演示站点，播放器仅为展示用途，不提供真实视频播放接口。
            </p>
          </div>
        </div>

        {/* Side column */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-20">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
              {category && (
                <Link
                  to={`/category/${category.id}`}
                  className="text-xs text-foreground-500 transition-colors hover:text-primary-300"
                >
                  更多 {category.name}
                </Link>
              )}
            </div>

            {related.length === 0 ? (
              <p className="rounded-lg border border-dashed border-background-300/70 bg-background-100 px-4 py-8 text-center text-sm text-foreground-500">
                暂无相关推荐
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {related.map((v) => (
                  <Link
                    key={v.id}
                    to={`/detail/${v.id}`}
                    className="group flex gap-3 rounded-lg border border-background-200/70 bg-background-100 p-2.5 transition-colors hover:border-primary-700"
                  >
                    <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={v.cover}
                        alt={`${v.title} - 成人快手短视频封面`}
                        title={`${v.title} - 成人快手`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-300">
                        {v.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">
                        {v.author} · {formatCount(v.views)} 次播放
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <div className="mt-6">
              <Link
                to="/favorites"
                className="flex items-center justify-between rounded-lg border border-background-200/70 bg-background-100 px-4 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-700"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-heart-3-line text-primary-300" />
                  查看我的收藏
                </span>
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}